import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AnalyticsEvent } from "@vega-api/custom/analytics/analyticsEvents";
import { useAnalytics } from "@vega-api/custom/hooks/useAnalytics";
import { getGetDealShortlistQueryKey } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import {
  useSendDealShortlistTeaserToLimitedPartners,
  useSendDealShortlistTeaserWithCustomUserList,
} from "@vega-api/generated/coinvest-deal-shortlist-actions/coinvest-deal-shortlist-actions";
import { type CoinvestPerson } from "@vega-api/generated/types/coinvestPerson";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type UseSendEmailMutationOptions = {
  dealId: string;
  onSuccess?: () => void;
  onError?: () => void;
  shortlistItemToLimitedPartnerMap?: Record<string, string | undefined>;
};

export const useSendEmailMutation = ({
  dealId,
  onSuccess,
  onError,
  shortlistItemToLimitedPartnerMap = {},
}: UseSendEmailMutationOptions) => {
  const { trackEvent } = useAnalytics();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const sendBulkEmailMutation = useSendDealShortlistTeaserToLimitedPartners({
    mutation: {
      onSuccess: (_, variables) => {
        // Track outreach email sent for each prospect
        variables.data.shortlistItemIds.forEach((prospectId) => {
          const limitedPartnerId = shortlistItemToLimitedPartnerMap[prospectId];

          if (limitedPartnerId) {
            trackEvent(AnalyticsEvent.COINVEST_OUTREACH_EMAIL_SENT, undefined, {
              dealId,
              limitedPartnerId,
            });
          }
        });

        showToast({
          title: m.status_success(),
          description: m.coInvestments_outreach_emailSentSuccess(),
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
        onSuccess?.();
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_sendEmail(),
        });

        onError?.();
      },
    },
  });

  const sendSingleEmailMutation = useSendDealShortlistTeaserWithCustomUserList({
    mutation: {
      onSuccess: (_, variables) => {
        const limitedPartnerId =
          shortlistItemToLimitedPartnerMap[variables.dealShortlistItemId];

        if (limitedPartnerId) {
          trackEvent(AnalyticsEvent.COINVEST_OUTREACH_EMAIL_SENT, undefined, {
            dealId,
            limitedPartnerId,
          });
        }

        showToast({
          title: m.status_success(),
          description: m.coInvestments_outreach_emailSentSuccess(),
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
        onSuccess?.();
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_sendEmail(),
        });

        onError?.();
      },
    },
  });

  const sendBulkEmail = useCallback(
    (prospectIds: string[], emailBody?: string, emailSubject?: string) => {
      sendBulkEmailMutation.mutate({
        dealId,
        data: {
          shortlistItemIds: prospectIds,
          ...(emailBody && { emailBody }),
          ...(emailSubject && { emailSubject }),
        },
      });
    },
    [dealId, sendBulkEmailMutation],
  );

  const sendSingleEmail = useCallback(
    ({
      dealShortlistItemId,
      emailBody,
      emailSubject,
      mainRecipients,
      ccRecipients,
      opportunityOwner,
    }: {
      dealShortlistItemId: string;
      emailBody: string;
      emailSubject: string;
      mainRecipients: CoinvestPerson[];
      ccRecipients: CoinvestPerson[];
      opportunityOwner: CoinvestPerson;
    }) => {
      sendSingleEmailMutation.mutate({
        dealId,
        dealShortlistItemId,
        data: {
          mainRecipients,
          ccRecipients,
          opportunityOwner,
          emailSubject,
          emailBody,
        },
      });
    },
    [dealId, sendSingleEmailMutation],
  );

  return {
    sendBulkEmail,
    sendSingleEmail,
    isPending:
      sendBulkEmailMutation.isPending || sendSingleEmailMutation.isPending,
  };
};
